import React from 'react';
import { Upload, ChevronUp, ChevronDown } from 'lucide-react';

export const Section = ({ title, description, children }) => (
  <div className="bg-white rounded-xl shadow-sm p-6">
    <h3 className="text-lg font-semibold text-slate-900 mb-1">{title}</h3>
    {description && <p className="text-sm text-slate-500 mb-6">{description}</p>}
    <div className="space-y-4">{children}</div>
  </div>
);

export const ContentSection = ({ title, expanded, onToggle, children }) => (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
    <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
    </button>
    {expanded && <div className="p-4 pt-0 space-y-4 border-t border-slate-100">{children}</div>}
  </div>
);

export const TextField = ({ label, value, onChange, type = 'text', placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testId}
      className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm" />
  </div>
);

export const TextArea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows}
      className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm resize-none" />
  </div>
);

export const SelectField = ({ label, value, options, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm">
      {options.map(opt => typeof opt === 'string' ? <option key={opt} value={opt}>{opt}</option> : <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

export const ColorField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg border-2 border-slate-200 cursor-pointer" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono text-sm" />
    </div>
  </div>
);

export const ImageField = ({ label, value, onChange, onUpload, description }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {description && <p className="text-xs text-slate-400 mb-2">{description}</p>}
    <div className="flex gap-4 items-start">
      <div className="w-32 h-24 border-2 border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex-shrink-0">
        {value && <img src={value} alt={label} className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 space-y-2">
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL"
          className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-slate-900">
          <Upload className="w-4 h-4" /> Upload
          <input type="file" accept="image/*" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) { const url = await onUpload(file); if (url) onChange(url); }
          }} className="hidden" />
        </label>
      </div>
    </div>
  </div>
);

export const ToggleSwitch = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200">
    <span className="font-medium text-slate-900">{label}</span>
    <button onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors ${checked ? 'bg-green-500' : 'bg-slate-300'}`}>
      <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'left-6' : 'left-1'}`} />
    </button>
  </div>
);
